import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../service/firebase";
import "../styles/Contact.css";

import linkedinIcon from "../assets/linkedin.png";
import githubIcon from "../assets/github.png";
import gmailIcon from "../assets/gmail.png";

/* -----------------------------
   Skeleton Loader
----------------------------- */
const ContactSkeleton = ({ isDarkMode }) => (
  <div
    className={`contact-loading-skeleton ${
      isDarkMode ? "sk-dark" : "sk-light"
    }`}
    aria-hidden="true"
  >
    <div className="contact-sk-title" />
    <div className="contact-sk-arrow" />
    <div className="contact-sk-icons">
      <div className="contact-sk-circle" />
      <div className="contact-sk-circle" />
      <div className="contact-sk-circle" />
    </div>
  </div>
);

const Contact = ({ isDarkMode }) => {
  const [links, setLinks] = useState({
    linkedin: "",
    github: "",
    gmail: "",
  });
  const [loading, setLoading] = useState(true);
  const [copyStatus, setCopyStatus] = useState("Email");

  const isMounted = useRef(true);

  /* -----------------------------
     Fetch Contact Data
  ----------------------------- */
  useEffect(() => {
    isMounted.current = true;

    const fetchContactInfo = async () => {
      try {
        const contactRef = doc(db, "Portfolio", "Contact");
        const contactSnap = await getDoc(contactRef);

        if (contactSnap.exists() && isMounted.current) {
          const data = contactSnap.data();
          setLinks({
            linkedin: data.linkedin || "",
            github: data.github || "",
            gmail: data.gmail || "",
          });
        }
      } catch (error) {
        console.error("Failed to load contact info:", error);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchContactInfo();
    return () => {
      isMounted.current = false;
    };
  }, []);

  /* -----------------------------
     Gmail Smart Handler
  ----------------------------- */
  const handleGmailAction = (e) => {
    e.preventDefault();
    if (!links.gmail) return;

    const email = links.gmail.replace(/^mailto:/i, "");
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`;

    window.open(gmailUrl, "_blank", "noopener,noreferrer");

    navigator.clipboard.writeText(email).catch(() => {});
    setCopyStatus("Opening Gmail...");
    setTimeout(() => setCopyStatus("Email"), 3000);
  };

  /* -----------------------------
     Safe URL Formatter
  ----------------------------- */
  const getSafeHref = (type, value) => {
    if (!value) return "#";
    const clean = value.trim();
    if (type === "linkedin" || type === "github") {
      return clean.startsWith("http") ? clean : `https://${clean}`;
    }
    return clean;
  };

  const themeClass = isDarkMode ? "theme-dark" : "theme-light";

  return (
    <section
      className={`contact-main-section ${themeClass}`}
      id="contact"
    >
      <div className="contact-content-wrapper">
        {loading ? (
          <ContactSkeleton isDarkMode={isDarkMode} />
        ) : (
          <>
            <header className="contact-hero-header">
              <h2 className="contact-heading-title">
                Feel free to contact me
              </h2>
              <p className="contact-heading-subtitle">
                I'm always open to discussing new projects.
              </p>
            </header>

            <div className="contact-links-grid">
              {/* LinkedIn */}
              <a
                href={getSafeHref("linkedin", links.linkedin)}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-social-btn btn-linkedin"
              >
                <img
                  src={linkedinIcon}
                  alt="LinkedIn"
                  className="contact-btn-img"
                />
                <span className="contact-tooltip-text">
                  LinkedIn
                </span>
              </a>

              {/* Gmail */}
              <a
                href={`mailto:${links.gmail}`}
                onClick={handleGmailAction}
                className="contact-social-btn btn-gmail"
              >
                <img
                  src={gmailIcon}
                  alt="Gmail"
                  className="contact-btn-img"
                />
                <span className="contact-tooltip-text">
                  {copyStatus}
                </span>
              </a>

              {/* GitHub */}
              <a
                href={getSafeHref("github", links.github)}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-social-btn btn-github"
              >
                <img
                  src={githubIcon}
                  alt="GitHub"
                  className="contact-btn-img"
                />
                <span className="contact-tooltip-text">
                  GitHub
                </span>
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Contact;
